import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Keyword {
  id: string
  keyword: string
  search_volume: number
  current_position: number
  previous_position: number
  difficulty: number
  url: string
  traffic_potential: number
}

interface Page {
  id: string
  url: string
  title: string
  page_score: number
  organic_traffic: number
  bounce_rate: number
  avg_time_on_page: number
  backlinks_count: number
  issues_count: number
}

interface Competitor {
  id: string
  domain: string
  domain_authority: number
  organic_traffic: number
  keywords_count: number
  backlinks_count: number
}

export default function SEOAnalyticsPage() {
  const [keywords, setKeywords] = useState<Keyword[]>([])
  const [pages, setPages] = useState<Page[]>([])
  const [competitors, setCompetitors] = useState<Competitor[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [keywordsRes, pagesRes, competitorsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/seo/keywords'),
      fetch(API_BASE + '/seo/pages'),
      fetch(API_BASE + '/seo/competitors'),
      fetch(API_BASE + '/seo/statistics')
    ])
    if (keywordsRes.ok) setKeywords((await keywordsRes.json()).keywords || [])
    if (pagesRes.ok) setPages((await pagesRes.json()).pages || [])
    if (competitorsRes.ok) setCompetitors((await competitorsRes.json()).competitors || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getPositionChange = (current: number, previous: number) => {
    const change = previous - current
    if (change > 0) return <span className="text-green-600">↑{change}</span>
    if (change < 0) return <span className="text-red-600">↓{Math.abs(change)}</span>
    return <span className="text-gray-400">–</span>
  }

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 30) return 'bg-green-100 text-green-800'
    if (difficulty <= 60) return 'bg-yellow-100 text-yellow-800'
    return 'bg-red-100 text-red-800'
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600'
    if (score >= 60) return 'text-yellow-600'
    return 'text-red-600'
  }

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K'
    return num.toString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">SEO Analytics</h1>
          <div className="space-x-2">
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Run Audit</button>
            <button className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">Export Report</button>
          </div>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Domain Authority</div>
              <div className="text-2xl font-bold">{statistics.domain_authority}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Organic Traffic</div>
              <div className="text-2xl font-bold">{formatNumber(statistics.organic_traffic)}</div>
              <div className="text-xs text-green-600">+{statistics.traffic_growth}%</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Keywords Ranked</div>
              <div className="text-2xl font-bold">{formatNumber(statistics.keywords_ranked)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Backlinks</div>
              <div className="text-2xl font-bold">{formatNumber(statistics.total_backlinks)}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Page Score</div>
              <div className={`text-2xl font-bold ${getScoreColor(statistics.avg_page_score)}`}>{statistics.avg_page_score}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-8">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Keyword Rankings</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Volume</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Difficulty</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {keywords.slice(0, 10).map(kw => (
                  <tr key={kw.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{kw.keyword}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{kw.url}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">#{kw.current_position}</span>
                        {getPositionChange(kw.current_position, kw.previous_position)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{formatNumber(kw.search_volume)}</div>
                      <div className="text-xs text-gray-500">{formatNumber(kw.traffic_potential)} potential</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getDifficultyColor(kw.difficulty)}`}>
                        {kw.difficulty}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Competitors</h3>
            <div className="space-y-3">
              {competitors.map(comp => (
                <div key={comp.id} className="p-3 bg-gray-50 rounded">
                  <div className="font-medium text-sm mb-1">{comp.domain}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">DA:</span>
                      <span className="font-bold ml-1">{comp.domain_authority}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Traffic:</span>
                      <span className="font-bold ml-1">{formatNumber(comp.organic_traffic)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Keywords:</span>
                      <span className="font-bold ml-1">{formatNumber(comp.keywords_count)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Backlinks:</span>
                      <span className="font-bold ml-1">{formatNumber(comp.backlinks_count)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Page Performance</h2></div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traffic</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Metrics</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {pages.slice(0, 8).map(page => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-sm max-w-xs truncate">{page.title}</div>
                    <div className="text-xs text-gray-500 truncate max-w-xs">{page.url}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-2xl font-bold ${getScoreColor(page.page_score)}`}>{page.page_score}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium">{formatNumber(page.organic_traffic)}</div>
                    <div className="text-xs text-gray-500">{page.backlinks_count} backlinks</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs">
                      <div>Bounce: {page.bounce_rate}%</div>
                      <div>Time: {Math.floor(page.avg_time_on_page / 60)}m {page.avg_time_on_page % 60}s</div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {page.issues_count > 0 ? (
                      <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">{page.issues_count} issues</span>
                    ) : (
                      <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">No issues</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  )
}
